import { AudioEngineRemoteAudio, P2pRemoteAudio, SfuRemoteAudio } from '../../RoomContext'
import { useContext, useEffect, useMemo, useState } from 'react'
import FakeMediaStreamTrack from '../../core/AudioEngine/FakeMediaStreamTrack'
import { AudioInputStreamInfo, UserContext, UserContextType, useUserContext } from '../../UserContext'
import useAppContextRef from '../../services/useAppContextRef'
import { AudioEngineRemoteStreamError, getAudioEngineRemoteDeviceError } from '../../Logic'
import { AudioEngineRemoteStreamStats, getAudioEngineInstance, AudioPatch } from '../../core/AudioEngine'
import { Trans, useTranslation } from 'react-i18next'
import { getAudioQualityPresets } from '../../services/Constants'
import { MusePeer } from '../../services/MusePeer'
import { MusePeer as RoomContextMusePeer } from '../../services/RoomContextMusePeer'
import { patcher } from '../../core/AudioEngine/WebAudioPatcher'
import { logger } from '../../services/Logger'
import { Button } from '@/UIComponents2/Button'
import { Alert } from '@/UIComponents2/Alert'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import { Username } from '@/components/Username'

const log = logger.createChildLogger({ name: 'RemoteAudioSettingsContent' })

export default function RemoteAudioSettingsContent({
    peer,
    media,
    onClose,
}: {
    peer: MusePeer | RoomContextMusePeer
    media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    onClose: () => void
}) {
    const { state } = useContext(UserContext) as UserContextType
    const qualityText = useQualityPresetDescription(media)

    const { t } = useTranslation()

    const audioEngineError = (() => {
        if (media.track instanceof FakeMediaStreamTrack) {
            return getAudioEngineRemoteDeviceError(state.mediaStreams.audioEngineAudioOutputError, media.track)
        } else {
            return null
        }
    })()

    let username
    let displayName
    let isGuest
    if (peer instanceof MusePeer) {
        username = peer.username
    } else {
        username = peer.username
        displayName = peer.displayName
        isGuest = peer.isGuest
    }

    return (
        <div className="w-full">
            <DialogBody>
                <DialogTitle noPadding>
                    <Trans
                        i18nKey="<0/>'s Device Info"
                        components={[<Username username={username} displayName={displayName} isGuest={isGuest} />]}
                    />
                </DialogTitle>
                <div className="t-subtitle-dialog">{media.info.displayLabel || '-'}</div>

                <div className="pt-6 flex flex-col gap-4">
                    <div>
                        <div className="t-base-bold pb-2">Info</div>
                        <div className="flex mb-1">
                            <div className="mr-2">Quality:</div>
                            <div className="text-muse-yellow">{qualityText}</div>
                        </div>
                        {media.track instanceof MediaStreamTrack && (
                            <WebRtcTrackStatsSection track={media.track} info={media.info} />
                        )}
                        {audioEngineError?.code === 'audio_engine_output_device_error' ||
                        audioEngineError?.code === 'audio_engine_not_supported_error' ? (
                            <Alert variant="solid" level="danger" className="ml-8 my-2">
                                {(audioEngineError?.message || '').split('\n').map((line, i) => {
                                    return <p key={i}>{line}</p>
                                })}
                            </Alert>
                        ) : (
                            <>
                                {media.track instanceof FakeMediaStreamTrack && (
                                    <AudioEngineTrackStatsSection
                                        track={media.track}
                                        info={media.info}
                                        audioEngineError={audioEngineError}
                                    />
                                )}
                            </>
                        )}
                    </div>

                    {state.profile?.policy?.canUseAudioPatching && (
                        <PatchControls track={media.track} info={media.info} />
                    )}
                </div>
            </DialogBody>

            <DialogActionButtonsSection rightButtonText={t('OK')} onRightButtonClicked={onClose} />
        </div>
    )
}

function AudioEngineTrackStatsSection({
    track,
    info,
    audioEngineError,
}: {
    track: FakeMediaStreamTrack
    info: AudioInputStreamInfo
    audioEngineError: AudioEngineRemoteStreamError | null
}) {
    const { appStateRef } = useAppContextRef()

    const [stats, set_stats] = useState<AudioEngineRemoteStreamStats | null>(null)

    const showAdvanced = appStateRef.current.appState.isInDevMode || false

    useEffect(() => {
        const t = setInterval(() => {
            set_stats(track.remoteStats)
        }, 200)
        return () => {
            clearInterval(t)
        }
    }, [])

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div>Sample rate:</div>
            <div>
                <div className="text-muse-yellow">{info.sampleRate}</div>
            </div>

            <div>
                <div>Target buffer size:</div>
            </div>
            <div>
                <div className="text-muse-yellow">{info.receiverBufferSizeMs} ms</div>
            </div>

            <div className="col-span-2" style={{ borderBottom: '1px solid #555555' }}></div>

            <div>
                <div>Connection state:</div>
            </div>
            <div>
                <div className="text-muse-yellow">{(stats?.connectionState ?? '-').toLowerCase()}</div>
            </div>

            {<NetworkConnectionErrorAlert stats={stats} audioEngineError={audioEngineError} />}

            <div>
                <div>Dropped audio (last 3s):</div>
            </div>
            <div>
                <div className="text-muse-yellow">{stats?.pctLostAudio3s ? stats.pctLostAudio3s.toFixed(1) : '0'}%</div>
            </div>

            {audioEngineError?.code === 'audio_engine_high_audio_dropout' && (
                <div className="col-span-2">
                    <Alert variant="solid" level="danger" className="ml-8 mb-2">
                        {audioEngineError?.message}
                    </Alert>
                </div>
            )}

            <div>
                <div>Current buffer position:</div>
            </div>
            <div>
                <div className="text-muse-yellow">{stats?.bufferHeadMs ?? '-'} ms</div>
            </div>

            <div>
                <div>Buffer size:</div>
            </div>
            <div>
                <div className="text-muse-yellow">{stats?.bufferWaterLineMs ?? '-'} ms</div>
            </div>

            {showAdvanced && <AdvancedStats stats={stats} />}
        </div>
    )
}

function WebRtcTrackStatsSection({ info, track }: { info: AudioInputStreamInfo; track: MediaStreamTrack }) {
    const [stats, set_stats] = useState<{ bitDepth: number; sampleRate: number; channelCount: number }>({
        bitDepth: 16,
        sampleRate: 44100,
        channelCount: 2,
    })

    useEffect(() => {
        const rtcInfo = track.getSettings()
        const bitDepth = rtcInfo.sampleSize ?? 16
        const sampleRate = rtcInfo.sampleRate ?? 44100
        const channelCount = info.channelCount

        set_stats({ bitDepth, sampleRate, channelCount })
    }, [])

    return (
        <>
            <div className="flex ml-4">
                <div className="mr-2">- Sample Rate:</div>
                <div className="text-muse-yellow">{stats.sampleRate}</div>
            </div>

            <div className="flex ml-4">
                <div className="mr-2">- Bit Depth:</div>
                <div className="text-muse-yellow">{stats.bitDepth}</div>
            </div>

            <div className="flex ml-4">
                <div className="mr-2">- Channels:</div>
                <div className="text-muse-yellow">{stats.channelCount}</div>
            </div>
        </>
    )
}

function NetworkConnectionErrorAlert({
    stats,
    audioEngineError,
}: {
    stats: AudioEngineRemoteStreamStats | null
    audioEngineError: AudioEngineRemoteStreamError | null
}) {
    const [showMoreInfo, set_showMoreInfo] = useState(false)

    if (!stats) {
        return null
    }

    if (audioEngineError?.code === 'audio_engine_connection_error') {
        return (
            <Alert variant="solid" level="danger" className="col-span-2">
                <p>{audioEngineError?.message}</p>
                {!showMoreInfo && (
                    <Button variant="link" onClick={() => set_showMoreInfo(true)}>
                        More info
                    </Button>
                )}
                {showMoreInfo && (
                    <>
                        {(audioEngineError?.messageDetails || '').split('\n').map((line, i) => {
                            return <p key={i}>{line}</p>
                        })}
                    </>
                )}
            </Alert>
        )
    }

    return null
}

function AdvancedStats({ stats }: { stats: AudioEngineRemoteStreamStats | null }) {
    const packetStatus = () => {
        if (getAudioEngineInstance().implementation === 'native') {
            return (
                <>
                    <div>Packet reception retry:</div>
                    <div className="flex flex-col">
                        <div className="text-muse-yellow">
                            {stats?.recvPacketRetryReqdCt ?? '-'} / {stats?.recvPacketRetrySuccessCt ?? '-'} /{' '}
                            {stats?.recvPacketRetryLateCt ?? '-'} / {stats?.recvPacketRetryLostCt ?? '-'}
                        </div>
                        <div className="text-gray-400 text-xs">(req'd / success / late / lost)</div>
                    </div>
                </>
            )
        }

        return (
            <>
                <div>Packet stats:</div>
                <div className="flex flex-col">
                    <div className="text-muse-yellow">
                        {stats?.latePacketCt ?? '0'} / {stats?.duplicatePacketCt ?? '0'} /{' '}
                        {stats?.outOfOrderPacketCt ?? '0'}
                    </div>
                    <div className="text-gray-400 text-xs">(late / dup / out-of-order)</div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="col-span-2" style={{ borderBottom: '1px solid #555555' }}></div>

            <div>
                <div>Buffer Overflows (recent / total):</div>
            </div>
            <div>
                <div className="text-muse-yellow">
                    {stats?.recentOverflowCt ?? '0'} / {stats?.totalOverflowCt ?? '0'}
                </div>
            </div>

            <div>
                <div>Hard Overflows:</div>
            </div>
            <div>
                <div className="text-muse-yellow">{stats?.hardOverflowCt ?? '0'}</div>
            </div>

            <div>
                <div>Buffer Underflows (recent / total):</div>
            </div>
            <div>
                <div className="text-muse-yellow">
                    {stats?.recentUnderflowCt ?? '0'} / {stats?.totalUnderflowCt ?? '0'}
                </div>
            </div>

            {packetStatus()}
        </>
    )
}

const dividerStyle = { margin: '10px 0', borderBottom: 'solid 1px var(--border-color-light)' }
function Divider() {
    return <div style={dividerStyle}></div>
}

function useQualityPresetDescription(media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio): string {
    const { t } = useTranslation()
    const qualityPresets = useMemo(() => getAudioQualityPresets(), [])

    const qualityText = useMemo(() => {
        let qualityCode = ''
        let prefix = ''
        if (media.info.isAudioEngine) {
            if (media.info.isCompressed) {
                prefix = t('Professional audio (compressed)')
                if (media.info.compressionBitrate <= 96_000) {
                    qualityCode = 'pro_essential'
                } else if (media.info.compressionBitrate <= 192_000) {
                    qualityCode = 'pro_enhanced'
                } else {
                    qualityCode = 'pro_hd'
                }
            } else {
                prefix = t('Professional audio (uncompressed)')
                if (media.info.bitDepth <= 16) {
                    qualityCode = 'pro_cd'
                } else if (media.info.bitDepth <= 24) {
                    qualityCode = 'pro_studio'
                } else {
                    qualityCode = 'pro_highest'
                }
            }
        } else {
            if (media.info.isHighQuality) {
                qualityCode = 'enhanced'
            } else {
                qualityCode = 'voice'
            }
        }

        const desc: string = qualityPresets.find((p) => p.code === qualityCode)?.menuItemLabel ?? t('Unknown')
        if (prefix !== '') {
            return `${prefix} - ${desc}`
        } else {
            return desc
        }
    }, [media.info.isAudioEngine, media.info.isCompressed, media.info.bitDepth, media.info.compressionBitrate])

    return qualityText
}

function PatchControls({
    track,
    info,
}: {
    track: FakeMediaStreamTrack | MediaStreamTrack
    info: AudioInputStreamInfo
}) {
    const [userState] = useUserContext()

    const {
        selectedDevice,
        selectedChannel,
        active,
        handleDeviceChange,
        handleChannelChange,
        handleRemove,
        selectedDeviceMaxChannels,
    } = useAudioPatcher(track, info.isAudioEngine ? info.audioEngineStreamUuid! : track.id)

    const channelSelectionItems = useMemo(() => {
        const maxChannels = selectedDeviceMaxChannels - (info.channelCount - 1)

        const items = Array.from({ length: maxChannels }, (_, i) => i).map((ch) => {
            return {
                label: info.channelCount > 1 ? `${ch + 1}-${ch + info.channelCount}` : `${ch + 1}`,
                value: ch + '',
            }
        })
        items.unshift({
            label: 'None',
            value: '_none',
        })
        return items
    }, [info, selectedDeviceMaxChannels])

    const audioOutputsSelectItems = useMemo(() => {
        const items = userState.mediaSources.audioOutputs.map((out) => {
            return {
                label: out.label,
                value: out.deviceId,
            }
        })
        items.unshift({
            label: 'None',
            value: '_none',
        })
        return items
    }, [userState.mediaSources.audioOutputs])

    return (
        <div className="border-t border-t-white/5 pt-4">
            <div className="t-base-bold pb-2">Aux Output</div>
            <div className="grid grid-cols-[130px_1fr] gap-2 items-center justify-center">
                <div>Output Device</div>
                <SimpleSelect
                    value={selectedDevice ?? '_none'}
                    items={audioOutputsSelectItems}
                    onChange={(v) => handleDeviceChange(v)}
                />
                <div>Channel</div>
                <SimpleSelect
                    value={selectedChannel == null ? '_none' : selectedChannel + ''}
                    items={channelSelectionItems}
                    disabled={!selectedDevice}
                    onChange={(v) => handleChannelChange(v)}
                />

                <div></div>
                <div>
                    {active && (
                        <Button className="text-muse-yellow" onClick={() => handleRemove()}>
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

type AudioPatcherHook = {
    selectedDevice: string | undefined
    selectedChannel: number | undefined
    active: boolean
    handleDeviceChange: (v: string) => void
    handleChannelChange: (v: string) => void
    handleRemove: () => void
    selectedDeviceMaxChannels: number
}

function useAudioPatcher(track: FakeMediaStreamTrack | MediaStreamTrack, streamId: string): AudioPatcherHook {
    const currentAERoom = getAudioEngineInstance().getCurrentRoom()

    const [selectedDevice, setSelectedDevice] = useState<string>()
    const [selectedChannel, setSelectedChannel] = useState<number>()
    const [active, setActive] = useState<boolean>(false)

    function listAudioPatches(): AudioPatch[] {
        if (track instanceof FakeMediaStreamTrack) {
            return currentAERoom?.listAudioPatches() ?? []
        } else {
            const patches = []
            for (const [_streamId, p] of patcher.patches) {
                patches.push({
                    sourceStreamId: p.sourceStreamId,
                    targetDeviceId: p.targetDeviceId,
                    targetChannel: p.targetChannel,
                })
            }

            return patches
        }
    }

    function removePatch(): void {
        if (track instanceof FakeMediaStreamTrack) {
            currentAERoom?.removeAudioPatch(streamId)
        } else {
            patcher.removePatch(streamId)
        }
    }

    function createPatch(selectedDevice: string, ch: number): void {
        if (track instanceof FakeMediaStreamTrack) {
            currentAERoom?.createAudioPatch(streamId, selectedDevice, ch)
        } else {
            patcher.addPatchFromTrack(track, selectedDevice, ch)
        }
    }

    useEffect(() => {
        const patch = listAudioPatches().find((p) => p.sourceStreamId === streamId)
        if (patch) {
            setActive(true)
            setSelectedDevice(patch.targetDeviceId)
            setSelectedChannel(patch.targetChannel)
            log.info('PatchControls', 'initialized patch state', patch)
        }
    }, [])

    const maxChannels = useMemo(() => {
        if (!selectedDevice) {
            return 0
        }
        return patcher.getMaxOutputChannels(selectedDevice)
    }, [selectedDevice])

    const handleDeviceChange = (v: string) => {
        setSelectedDevice(!v || v === '_none' ? undefined : v)
        setSelectedChannel(undefined)
        if (active) {
            removePatch()
            setActive(false)
        }
        log.info('PatchControls', 'selected device', { device: v, active: false })
    }

    const handleChannelChange = (v: string) => {
        if (!selectedDevice) {
            return
        }
        if (!v || v === '_none') {
            log.info('PatchControls', 'unselected channel')
            setSelectedChannel(undefined)
            if (active) {
                removePatch()
                setActive(false)
            }
            return
        }

        const ch = parseInt(v)
        if (ch >= 0 && ch < maxChannels) {
            log.info('PatchControls', 'selected channel', { ch })
            if (active) {
                log.info('PatchControls', 'removing previous patch')
                removePatch()
            } else {
                setActive(true)
            }
            setSelectedChannel(ch)
            log.info('PatchControls', 'setting new patch')
            createPatch(selectedDevice, ch)
        }
    }

    const handleRemove = () => {
        if (active) {
            setSelectedDevice(undefined)
            setSelectedChannel(undefined)
            setActive(false)
            removePatch()
        }
    }

    return {
        selectedDevice,
        selectedChannel,
        active,
        handleDeviceChange,
        handleChannelChange,
        handleRemove,
        selectedDeviceMaxChannels: maxChannels,
    }
}
